// src/pages/NomCatalogosPage.jsx
// Catálogos de nómina: Departamentos, Puestos y Registros Patronales en pestañas
import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerDepartamentos, crearDepartamento, actualizarDepartamento, eliminarDepartamento,
  obtenerPuestos, crearPuesto, actualizarPuesto, eliminarPuesto,
  obtenerRegPatronales, crearRegPatronal, actualizarRegPatronal, eliminarRegPatronal,
  obtenerTablasIsr, crearTablaIsr, actualizarTablaIsr, eliminarTablaIsr,
  obtenerSubsidioEmpleo, crearSubsidioEmpleo, actualizarSubsidioEmpleo, eliminarSubsidioEmpleo,
  obtenerSalariosMinimos, crearSalarioMinimo, actualizarSalarioMinimo, eliminarSalarioMinimo,
} from "../services/nominaService";

const TABS = ["Departamentos", "Puestos", "Registros Patronales", "Tablas ISR", "Subsidio al Empleo", "Salarios Mínimos / UMA"];
const TIPO_PERIODO_LABEL = { S: "Semanal", C: "Catorcenal", Q: "Quincenal", M: "Mensual" };
const money2 = (n) => Number(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ===== Componente genérico de catálogo simple (clave + nombre) =====
function SimpleCatalog({ rows, loading, onSave, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ Clave: "", Nombre: "", Activo: true });
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const open = (row = null) => {
    setEditRow(row);
    setForm(row ? { Clave: row.clave ?? row.Clave ?? "", Nombre: row.nombre ?? row.Nombre ?? "", Activo: row.activo ?? row.Activo ?? true } : { Clave: "", Nombre: "", Activo: true });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(editRow?.id ?? editRow?.Id ?? null, form);
      toastSuccess(editRow ? "Actualizado" : "Creado");
      setShowModal(false);
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await onDelete(id);
      toastSuccess("Eliminado");
    } catch {
      toastError("No se pudo eliminar");
    }
  };

  const columns = [
    { key: "clave", title: "Clave", style: { width: 120, fontWeight: 600 } },
    { key: "nombre", title: "Nombre" },
    {
      key: "activo", title: "Activo",
      render: (r) => (r.activo ?? r.Activo)
        ? <span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span>
        : <span style={{ color: "#cbd5e1" }}>—</span>,
      style: { width: 70, textAlign: "center" },
    },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => open(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id ?? r.Id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="lmd-btn lmd-btn-primary" onClick={() => open()}>+ Nuevo</button>
      </div>
      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 420, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editRow ? "Editar" : "Nuevo"}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Clave *</label>
                <input className="lmd-input" value={form.Clave} onChange={(e) => setForm((f) => ({ ...f, Clave: e.target.value }))}
                  required style={{ width: "100%" }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Nombre *</label>
                <input className="lmd-input" value={form.Nombre} onChange={(e) => setForm((f) => ({ ...f, Nombre: e.target.value }))}
                  required style={{ width: "100%" }} />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 20 }}>
                <input type="checkbox" checked={form.Activo} onChange={(e) => setForm((f) => ({ ...f, Activo: e.target.checked }))} />
                Activo
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Catálogo de Registros Patronales =====
function RegPatronalCatalog({ rows, loading, onSave, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ NumeroRegistro: "", Nombre: "", FactorRiesgoPuesto: "0.0054", Activo: true });
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const open = (row = null) => {
    setEditRow(row);
    setForm(row
      ? { NumeroRegistro: row.numeroRegistro, Nombre: row.nombre, FactorRiesgoPuesto: String(row.factorRiesgoPuesto), Activo: row.activo }
      : { NumeroRegistro: "", Nombre: "", FactorRiesgoPuesto: "0.0054", Activo: true });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(editRow?.id ?? null, { ...form, FactorRiesgoPuesto: parseFloat(form.FactorRiesgoPuesto) || 0.0054 });
      toastSuccess(editRow ? "Actualizado" : "Creado");
      setShowModal(false);
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await onDelete(id);
      toastSuccess("Eliminado");
    } catch {
      toastError("No se pudo eliminar");
    }
  };

  const columns = [
    { key: "numeroRegistro", title: "No. Registro", style: { fontWeight: 600, width: 160 } },
    { key: "nombre", title: "Nombre" },
    {
      key: "factorRiesgoPuesto", title: "Factor Riesgo",
      render: (r) => Number(r.factorRiesgoPuesto).toFixed(4),
      style: { width: 120, textAlign: "right" },
    },
    {
      key: "activo", title: "Activo",
      render: (r) => r.activo ? <span style={{ color: "#16a34a", fontWeight: 700 }}>✓</span> : <span style={{ color: "#cbd5e1" }}>—</span>,
      style: { width: 70, textAlign: "center" },
    },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => open(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="lmd-btn lmd-btn-primary" onClick={() => open()}>+ Nuevo</button>
      </div>
      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 460, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editRow ? "Editar Registro Patronal" : "Nuevo Registro Patronal"}</h3>
            <form onSubmit={handleSubmit}>
              {[
                { label: "No. Registro IMSS *", key: "NumeroRegistro", placeholder: "Y00-00000-00-0" },
                { label: "Nombre / Descripción *", key: "Nombre" },
                { label: "Factor de Riesgo de Trabajo", key: "FactorRiesgoPuesto", type: "number", placeholder: "0.0054" },
              ].map(({ label, key, placeholder, type }) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>{label}</label>
                  <input className="lmd-input" type={type || "text"} value={form[key]} required={label.includes("*")}
                    placeholder={placeholder}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    style={{ width: "100%" }} />
                </div>
              ))}
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 20 }}>
                <input type="checkbox" checked={form.Activo} onChange={(e) => setForm((f) => ({ ...f, Activo: e.target.checked }))} />
                Activo
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Catálogo de Tablas ISR =====
function TablaIsrCatalog({ anio, tipoPeriodo, onAnioChange, onTipoChange, rows, loading, onSave, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const EMPTY = { Anio: anio, TipoPeriodo: tipoPeriodo, LimiteInferior: "", LimiteSuperior: "", CuotaFija: "", PorcentajeSobreLimite: "" };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const open = (row = null) => {
    setEditRow(row);
    setForm(row
      ? { Anio: row.anio, TipoPeriodo: row.tipoPeriodo, LimiteInferior: row.limiteInferior, LimiteSuperior: row.limiteSuperior, CuotaFija: row.cuotaFija, PorcentajeSobreLimite: row.porcentajeSobreLimite }
      : { Anio: anio, TipoPeriodo: tipoPeriodo, LimiteInferior: "", LimiteSuperior: "", CuotaFija: "", PorcentajeSobreLimite: "" });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dto = {
        Anio: Number(form.Anio), TipoPeriodo: form.TipoPeriodo,
        LimiteInferior: parseFloat(form.LimiteInferior) || 0,
        LimiteSuperior: parseFloat(form.LimiteSuperior) || 0,
        CuotaFija: parseFloat(form.CuotaFija) || 0,
        PorcentajeSobreLimite: parseFloat(form.PorcentajeSobreLimite) || 0,
      };
      await onSave(editRow?.id ?? null, dto);
      toastSuccess(editRow ? "Actualizado" : "Creado");
      setShowModal(false);
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este renglón de la tabla?")) return;
    try { await onDelete(id); toastSuccess("Eliminado"); } catch { toastError("No se pudo eliminar"); }
  };

  const columns = [
    { key: "limiteInferior", title: "Límite Inferior", render: (r) => `$${money2(r.limiteInferior)}`, style: { textAlign: "right", width: 140 } },
    { key: "limiteSuperior", title: "Límite Superior", render: (r) => `$${money2(r.limiteSuperior)}`, style: { textAlign: "right", width: 140 } },
    { key: "cuotaFija", title: "Cuota Fija", render: (r) => `$${money2(r.cuotaFija)}`, style: { textAlign: "right", width: 120 } },
    { key: "porcentajeSobreLimite", title: "% Sobre Excedente", render: (r) => `${money2(r.porcentajeSobreLimite)}%`, style: { textAlign: "right", width: 140 } },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => open(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginRight: 6 }}>Año:</label>
            <input className="lmd-input" type="number" value={anio} onChange={(e) => onAnioChange(Number(e.target.value))} style={{ width: 90 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginRight: 6 }}>Periodicidad:</label>
            <select className="lmd-select" value={tipoPeriodo} onChange={(e) => onTipoChange(e.target.value)} style={{ width: 130 }}>
              {Object.entries(TIPO_PERIODO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <button className="lmd-btn lmd-btn-primary" onClick={() => open()}>+ Nuevo renglón</button>
      </div>

      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 440, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editRow ? "Editar renglón ISR" : "Nuevo renglón ISR"}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Año *</label>
                  <input className="lmd-input" type="number" value={form.Anio} required style={{ width: "100%" }}
                    onChange={(e) => setForm((f) => ({ ...f, Anio: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Periodicidad *</label>
                  <select className="lmd-select" value={form.TipoPeriodo} required style={{ width: "100%" }}
                    onChange={(e) => setForm((f) => ({ ...f, TipoPeriodo: e.target.value }))}>
                    {Object.entries(TIPO_PERIODO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              {[
                { label: "Límite Inferior *", key: "LimiteInferior" },
                { label: "Límite Superior *", key: "LimiteSuperior" },
                { label: "Cuota Fija *", key: "CuotaFija" },
                { label: "% Sobre Excedente *", key: "PorcentajeSobreLimite" },
              ].map(({ label, key }) => (
                <div key={key} style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>{label}</label>
                  <input className="lmd-input" type="number" step="0.01" value={form[key]} required style={{ width: "100%" }}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Catálogo de Subsidio al Empleo =====
function SubsidioCatalog({ anio, tipoPeriodo, onAnioChange, onTipoChange, rows, loading, onSave, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ Anio: anio, TipoPeriodo: tipoPeriodo, HastaIngreso: "", SubsidioMensual: "" });
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const open = (row = null) => {
    setEditRow(row);
    setForm(row
      ? { Anio: row.anio, TipoPeriodo: row.tipoPeriodo, HastaIngreso: row.hastaIngreso, SubsidioMensual: row.subsidioMensual }
      : { Anio: anio, TipoPeriodo: tipoPeriodo, HastaIngreso: "", SubsidioMensual: "" });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dto = {
        Anio: Number(form.Anio), TipoPeriodo: form.TipoPeriodo,
        HastaIngreso: parseFloat(form.HastaIngreso) || 0,
        SubsidioMensual: parseFloat(form.SubsidioMensual) || 0,
      };
      await onSave(editRow?.id ?? null, dto);
      toastSuccess(editRow ? "Actualizado" : "Creado");
      setShowModal(false);
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este renglón?")) return;
    try { await onDelete(id); toastSuccess("Eliminado"); } catch { toastError("No se pudo eliminar"); }
  };

  const columns = [
    { key: "hastaIngreso", title: "Hasta Ingreso de", render: (r) => `$${money2(r.hastaIngreso)}`, style: { textAlign: "right", width: 160 } },
    { key: "subsidioMensual", title: "Subsidio", render: (r) => `$${money2(r.subsidioMensual)}`, style: { textAlign: "right", width: 140 } },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => open(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginRight: 6 }}>Año:</label>
            <input className="lmd-input" type="number" value={anio} onChange={(e) => onAnioChange(Number(e.target.value))} style={{ width: 90 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginRight: 6 }}>Periodicidad:</label>
            <select className="lmd-select" value={tipoPeriodo} onChange={(e) => onTipoChange(e.target.value)} style={{ width: 130 }}>
              {Object.entries(TIPO_PERIODO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <button className="lmd-btn lmd-btn-primary" onClick={() => open()}>+ Nuevo renglón</button>
      </div>

      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 420, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editRow ? "Editar Subsidio" : "Nuevo Subsidio"}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Año *</label>
                  <input className="lmd-input" type="number" value={form.Anio} required style={{ width: "100%" }}
                    onChange={(e) => setForm((f) => ({ ...f, Anio: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Periodicidad *</label>
                  <select className="lmd-select" value={form.TipoPeriodo} required style={{ width: "100%" }}
                    onChange={(e) => setForm((f) => ({ ...f, TipoPeriodo: e.target.value }))}>
                    {Object.entries(TIPO_PERIODO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Hasta Ingreso de *</label>
                <input className="lmd-input" type="number" step="0.01" value={form.HastaIngreso} required style={{ width: "100%" }}
                  onChange={(e) => setForm((f) => ({ ...f, HastaIngreso: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Subsidio *</label>
                <input className="lmd-input" type="number" step="0.01" value={form.SubsidioMensual} required style={{ width: "100%" }}
                  onChange={(e) => setForm((f) => ({ ...f, SubsidioMensual: e.target.value }))} />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Catálogo de Salarios Mínimos / UMA =====
function SalarioMinimoCatalog({ rows, loading, onSave, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const anioActual = new Date().getFullYear();
  const [form, setForm] = useState({ Anio: anioActual, Zona: "G", SalarioMinimo: "", Uma: "", FechaVigencia: "" });
  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const open = (row = null) => {
    setEditRow(row);
    setForm(row
      ? { Anio: row.anio, Zona: row.zona, SalarioMinimo: row.salarioMinimo, Uma: row.uma, FechaVigencia: row.fechaVigencia ? row.fechaVigencia.slice(0, 10) : "" }
      : { Anio: anioActual, Zona: "G", SalarioMinimo: "", Uma: "", FechaVigencia: `${anioActual}-01-01` });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dto = {
        Anio: Number(form.Anio), Zona: form.Zona,
        SalarioMinimo: parseFloat(form.SalarioMinimo) || 0,
        Uma: parseFloat(form.Uma) || 0,
        FechaVigencia: form.FechaVigencia || null,
      };
      await onSave(editRow?.id ?? null, dto);
      toastSuccess(editRow ? "Actualizado" : "Creado");
      setShowModal(false);
    } catch {
      toastError("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este renglón?")) return;
    try { await onDelete(id); toastSuccess("Eliminado"); } catch { toastError("No se pudo eliminar"); }
  };

  const columns = [
    { key: "anio", title: "Año", style: { width: 80, fontWeight: 600 } },
    { key: "zona", title: "Zona", render: (r) => r.zona === "F" ? "Frontera Norte" : "General", style: { width: 140 } },
    { key: "salarioMinimo", title: "Salario Mínimo", render: (r) => `$${money2(r.salarioMinimo)}`, style: { textAlign: "right", width: 140 } },
    { key: "uma", title: "UMA Diaria", render: (r) => `$${money2(r.uma)}`, style: { textAlign: "right", width: 120 } },
    { key: "fechaVigencia", title: "Vigente desde", render: (r) => r.fechaVigencia ? new Date(r.fechaVigencia).toLocaleDateString("es-MX") : "", style: { width: 130 } },
    {
      key: "_acc", title: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => open(r)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id)}>Eliminar</button>
        </div>
      ),
      style: { width: 130 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="lmd-btn lmd-btn-primary" onClick={() => open()}>+ Nuevo renglón</button>
      </div>
      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 420, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editRow ? "Editar renglón" : "Nuevo renglón"}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Año *</label>
                  <input className="lmd-input" type="number" value={form.Anio} required style={{ width: "100%" }}
                    onChange={(e) => setForm((f) => ({ ...f, Anio: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Zona *</label>
                  <select className="lmd-select" value={form.Zona} required style={{ width: "100%" }}
                    onChange={(e) => setForm((f) => ({ ...f, Zona: e.target.value }))}>
                    <option value="G">General</option>
                    <option value="F">Frontera Norte</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Salario Mínimo Diario *</label>
                <input className="lmd-input" type="number" step="0.01" value={form.SalarioMinimo} required style={{ width: "100%" }}
                  onChange={(e) => setForm((f) => ({ ...f, SalarioMinimo: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>UMA Diaria *</label>
                <input className="lmd-input" type="number" step="0.01" value={form.Uma} required style={{ width: "100%" }}
                  onChange={(e) => setForm((f) => ({ ...f, Uma: e.target.value }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Vigente desde *</label>
                <input className="lmd-input" type="date" value={form.FechaVigencia} required style={{ width: "100%" }}
                  onChange={(e) => setForm((f) => ({ ...f, FechaVigencia: e.target.value }))} />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>
                  {saving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== PÁGINA PRINCIPAL =====
export default function NomCatalogosPage() {
  const [activeTab, setActiveTab] = useState(0);

  const [deptos, setDeptos] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [regPat, setRegPat] = useState([]);
  const [loadingD, setLoadingD] = useState(false);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingR, setLoadingR] = useState(false);

  const anioActual = new Date().getFullYear();
  const [isrAnio, setIsrAnio] = useState(anioActual);
  const [isrTipo, setIsrTipo] = useState("Q");
  const [tablasIsr, setTablasIsr] = useState([]);
  const [loadingIsr, setLoadingIsr] = useState(false);

  const [subAnio, setSubAnio] = useState(anioActual);
  const [subTipo, setSubTipo] = useState("Q");
  const [subsidios, setSubsidios] = useState([]);
  const [loadingSub, setLoadingSub] = useState(false);

  const [salMin, setSalMin] = useState([]);
  const [loadingSalMin, setLoadingSalMin] = useState(false);

  const loadDeptos = useCallback(async () => {
    setLoadingD(true);
    try { setDeptos(await obtenerDepartamentos()); } finally { setLoadingD(false); }
  }, []);

  const loadPuestos = useCallback(async () => {
    setLoadingP(true);
    try { setPuestos(await obtenerPuestos()); } finally { setLoadingP(false); }
  }, []);

  const loadReg = useCallback(async () => {
    setLoadingR(true);
    try { setRegPat(await obtenerRegPatronales()); } finally { setLoadingR(false); }
  }, []);

  const loadTablasIsr = useCallback(async () => {
    setLoadingIsr(true);
    try { setTablasIsr(await obtenerTablasIsr(isrAnio, isrTipo)); } finally { setLoadingIsr(false); }
  }, [isrAnio, isrTipo]);

  const loadSubsidios = useCallback(async () => {
    setLoadingSub(true);
    try { setSubsidios(await obtenerSubsidioEmpleo(subAnio, subTipo)); } finally { setLoadingSub(false); }
  }, [subAnio, subTipo]);

  const loadSalMin = useCallback(async () => {
    setLoadingSalMin(true);
    try { setSalMin(await obtenerSalariosMinimos()); } finally { setLoadingSalMin(false); }
  }, []);

  useEffect(() => { loadDeptos(); loadPuestos(); loadReg(); }, [loadDeptos, loadPuestos, loadReg]);
  useEffect(() => { loadTablasIsr(); }, [loadTablasIsr]);
  useEffect(() => { loadSubsidios(); }, [loadSubsidios]);
  useEffect(() => { loadSalMin(); }, [loadSalMin]);

  const saveDepto = async (id, form) => {
    if (id) { await actualizarDepartamento(id, form); } else { await crearDepartamento(form); }
    loadDeptos();
  };

  const savePuesto = async (id, form) => {
    if (id) { await actualizarPuesto(id, form); } else { await crearPuesto(form); }
    loadPuestos();
  };

  const saveReg = async (id, form) => {
    if (id) { await actualizarRegPatronal(id, form); } else { await crearRegPatronal(form); }
    loadReg();
  };

  const deleteDepto = async (id) => { await eliminarDepartamento(id); loadDeptos(); };
  const deletePuesto = async (id) => { await eliminarPuesto(id); loadPuestos(); };
  const deleteReg = async (id) => { await eliminarRegPatronal(id); loadReg(); };

  const saveTablaIsr = async (id, dto) => {
    if (id) { await actualizarTablaIsr(id, dto); } else { await crearTablaIsr(dto); }
    loadTablasIsr();
  };
  const deleteTablaIsr = async (id) => { await eliminarTablaIsr(id); loadTablasIsr(); };

  const saveSubsidio = async (id, dto) => {
    if (id) { await actualizarSubsidioEmpleo(id, dto); } else { await crearSubsidioEmpleo(dto); }
    loadSubsidios();
  };
  const deleteSubsidio = async (id) => { await eliminarSubsidioEmpleo(id); loadSubsidios(); };

  const saveSalMin = async (id, dto) => {
    if (id) { await actualizarSalarioMinimo(id, dto); } else { await crearSalarioMinimo(dto); }
    loadSalMin();
  };
  const deleteSalMin = async (id) => { await eliminarSalarioMinimo(id); loadSalMin(); };

  return (
    <div>
      <PageHeader title="Catálogos de Nómina" subtitle="Departamentos, puestos y registros patronales" />

      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid var(--clr-border)", marginBottom: 20 }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setActiveTab(i)} style={{
            padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
            fontSize: 14, fontWeight: activeTab === i ? 700 : 400,
            color: activeTab === i ? "var(--clr-primary)" : "var(--clr-text-muted)",
            borderBottom: activeTab === i ? "2px solid var(--clr-primary)" : "2px solid transparent",
            marginBottom: -2, transition: "all .2s",
          }}>
            {t}
          </button>
        ))}
      </div>

      {activeTab === 0 && (
        <SimpleCatalog rows={deptos} loading={loadingD} onSave={saveDepto} onDelete={deleteDepto} />
      )}
      {activeTab === 1 && (
        <SimpleCatalog rows={puestos} loading={loadingP} onSave={savePuesto} onDelete={deletePuesto} />
      )}
      {activeTab === 2 && (
        <RegPatronalCatalog rows={regPat} loading={loadingR} onSave={saveReg} onDelete={deleteReg} />
      )}
      {activeTab === 3 && (
        <TablaIsrCatalog
          anio={isrAnio} tipoPeriodo={isrTipo}
          onAnioChange={setIsrAnio} onTipoChange={setIsrTipo}
          rows={tablasIsr} loading={loadingIsr} onSave={saveTablaIsr} onDelete={deleteTablaIsr}
        />
      )}
      {activeTab === 4 && (
        <SubsidioCatalog
          anio={subAnio} tipoPeriodo={subTipo}
          onAnioChange={setSubAnio} onTipoChange={setSubTipo}
          rows={subsidios} loading={loadingSub} onSave={saveSubsidio} onDelete={deleteSubsidio}
        />
      )}
      {activeTab === 5 && (
        <SalarioMinimoCatalog rows={salMin} loading={loadingSalMin} onSave={saveSalMin} onDelete={deleteSalMin} />
      )}
    </div>
  );
}
